
import { Link } from 'react-router-dom';

export default function GenericMedicine() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Article Header */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-teal-600 font-semibold">ビースト君</p>
                <p className="text-gray-500 text-sm">2025年10月5日</p>
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">ジェネリック医薬品とは？違い・メリット・デメリットを薬剤師が解説</h1>
            <p className="text-xl text-gray-600">先発医薬品との違いやオーソライズドジェネリックについても詳しく説明します</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/6538d10345e7cf3e8dad1a8520d6a1af.jpg"
              alt="ジェネリック医薬品の解説"
              className="w-full max-w-2xl h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              こんにちは、ビースト君です！薬局で「ジェネリック医薬品に変更しますか？」と聞かれたことはありませんか？
              今日は薬剤師として、ジェネリック医薬品について詳しく解説します。筋トレでも正しい知識が大切なように、
              薬についても正しく理解することが重要です。
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">ジェネリック医薬品とは</h2>
            
            <p className="text-gray-700 mb-6">
              ジェネリック医薬品（後発医薬品）とは、先発医薬品（新薬）の特許が切れた後に、
              同じ有効成分を使って製造される医薬品のことです。筋トレで例えるなら、
              同じトレーニング効果を持つ別のメーカーの器具のようなものです。
            </p>

            <div className="bg-emerald-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-emerald-800 mb-4">ジェネリック医薬品の基本</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📋 定義</h4>
                  <p className="text-gray-600 text-sm">先発医薬品と同じ有効成分、同じ効果・効能を持つ医薬品</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">⏰ 開発期間</h4>
                  <p className="text-gray-600 text-sm">先発医薬品の特許切れ後（通常20-25年後）に製造開始</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🔬 承認基準</h4>
                  <p className="text-gray-600 text-sm">生物学的同等性試験により先発品との同等性を証明</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💰 価格</h4>
                  <p className="text-gray-600 text-sm">先発医薬品の約2-7割程度の価格設定</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">先発医薬品との違い</h2>
            
            <p className="text-gray-700 mb-6">
              ジェネリック医薬品と先発医薬品の違いを正しく理解することが大切です。
              筋トレでも器具の特徴を理解して使うように、薬の特徴を知って使いましょう。
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">主な違いの比較</h3>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">項目</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">先発医薬品</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">ジェネリック医薬品</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">有効成分</td>
                      <td className="px-4 py-3 text-gray-600">新規開発成分</td>
                      <td className="px-4 py-3 text-gray-600">先発品と同一</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">効果・効能</td>
                      <td className="px-4 py-3 text-gray-600">臨床試験で確認</td>
                      <td className="px-4 py-3 text-gray-600">先発品と同等</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">添加物</td>
                      <td className="px-4 py-3 text-gray-600">独自配合</td>
                      <td className="px-4 py-3 text-gray-600">異なる場合あり</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">外観</td>
                      <td className="px-4 py-3 text-gray-600">独自デザイン</td>
                      <td className="px-4 py-3 text-gray-600">異なるデザイン</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">価格</td>
                      <td className="px-4 py-3 text-gray-600">高価</td>
                      <td className="px-4 py-3 text-gray-600">安価（2-7割程度）</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">開発費</td>
                      <td className="px-4 py-3 text-gray-600">数百億円</td>
                      <td className="px-4 py-3 text-gray-600">数億円程度</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">ジェネリック医薬品のメリット</h2>
            
            <p className="text-gray-700 mb-6">
              ジェネリック医薬品には患者さんにとって多くのメリットがあります。
              筋トレでコストパフォーマンスの良い器具を選ぶように、薬でも経済的なメリットは大きいです。
            </p>

            <div className="bg-green-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-green-800 mb-4">主なメリット</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-money-dollar-circle-line text-green-600 mr-2"></i>
                    経済的負担の軽減
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">薬代が大幅に安くなり、家計への負担が軽減される</p>
                  <p className="text-xs text-gray-500">例：月1万円の薬代が3,000-7,000円程度に削減</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-hospital-line text-green-600 mr-2"></i>
                    医療費全体の削減
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">国の医療費削減により、保険制度の維持に貢献</p>
                  <p className="text-xs text-gray-500">効果：将来の保険料上昇抑制、医療制度の持続可能性向上</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-capsule-line text-green-600 mr-2"></i>
                    製剤工夫による改良
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">飲みやすさや安定性の向上など、先発品を上回る改良</p>
                  <p className="text-xs text-gray-500">例：小型化、味の改善、分割線の追加、包装の工夫</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-choice-line text-green-600 mr-2"></i>
                    選択肢の拡大
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">複数のメーカーから選択でき、患者に最適な製剤を選択可能</p>
                  <p className="text-xs text-gray-500">メリット：アレルギー対応、剤形の選択、価格の比較</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-time-line text-green-600 mr-2"></i>
                    長期使用での安心感
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">先発品で長期間の使用実績があり、安全性が確立</p>
                  <p className="text-xs text-gray-500">根拠：市販後調査データ、副作用情報の蓄積</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">ジェネリック医薬品のデメリット</h2>
            
            <p className="text-gray-700 mb-6">
              メリットがある一方で、デメリットや注意点もあります。
              筋トレでも器具の特徴を理解して使うように、薬の特徴を正しく理解することが大切です。
            </p>

            <div className="bg-orange-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-orange-800 mb-4">主なデメリット・注意点</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-question-line text-orange-600 mr-2"></i>
                    添加物の違いによる影響
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">添加物が異なるため、アレルギーや副作用の可能性</p>
                  <p className="text-xs text-gray-500">対策：アレルギー歴の確認、症状変化の観察</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-eye-line text-orange-600 mr-2"></i>
                    外観の変化による混乱
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">色や形が変わることで、患者さんが混乱する場合</p>
                  <p className="text-xs text-gray-500">対策：事前説明、お薬手帳での確認、薬剤師への相談</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-heart-pulse-line text-orange-600 mr-2"></i>
                    心理的な不安
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">「安いから効果が劣る」という先入観による不安</p>
                  <p className="text-xs text-gray-500">解決：正しい知識の提供、薬剤師による丁寧な説明</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-flask-line text-orange-600 mr-2"></i>
                    製剤間の微細な差
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">生物学的同等性の範囲内での個体差の可能性</p>
                  <p className="text-xs text-gray-500">注意：効果や副作用に変化を感じた場合は医師・薬剤師に相談</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                    <i className="ri-information-line text-orange-600 mr-2"></i>
                    情報量の違い
                  </h4>
                  <p className="text-gray-600 text-sm mb-2">先発品と比べて臨床データや使用経験が少ない場合</p>
                  <p className="text-xs text-gray-500">対応：市販後調査への協力、定期的な経過観察</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">オーソライズドジェネリック（AG）とは</h2>
            
            <p className="text-gray-700 mb-6">
              オーソライズドジェネリック（Authorized Generic：AG）は、特別なタイプのジェネリック医薬品です。
              筋トレで例えるなら、オリジナルメーカーが認めた公式ライセンス品のようなものです。
            </p>

            <div className="bg-purple-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">オーソライズドジェネリックの特徴</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🤝 先発メーカーとの関係</h4>
                  <p className="text-gray-600 text-sm mb-2">先発医薬品メーカーから許諾を得て製造・販売</p>
                  <p className="text-xs text-gray-500">特徴：原薬、製法、添加物が先発品と全く同一</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🔬 品質の同一性</h4>
                  <p className="text-gray-600 text-sm mb-2">先発品と全く同じ原薬・製造方法を使用</p>
                  <p className="text-xs text-gray-500">メリット：添加物による違いがなく、より安心</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">📋 承認プロセス</h4>
                  <p className="text-gray-600 text-sm mb-2">通常のジェネリックより簡略化された承認手続き</p>
                  <p className="text-xs text-gray-500">理由：先発品と同一のため、生物学的同等性試験が不要</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💰 価格設定</h4>
                  <p className="text-gray-600 text-sm mb-2">通常のジェネリックと同様の価格設定</p>
                  <p className="text-xs text-gray-500">位置づけ：品質は先発品同等、価格はジェネリック並み</p>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-indigo-800 mb-4">AGと通常のジェネリックの比較</h3>
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">項目</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">オーソライズドジェネリック</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">通常のジェネリック</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">原薬</td>
                      <td className="px-4 py-3 text-gray-600">先発品と完全同一</td>
                      <td className="px-4 py-3 text-gray-600">同等だが製造元が異なる場合あり</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">添加物</td>
                      <td className="px-4 py-3 text-gray-600">先発品と完全同一</td>
                      <td className="px-4 py-3 text-gray-600">異なる場合が多い</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">製造方法</td>
                      <td className="px-4 py-3 text-gray-600">先発品と同一</td>
                      <td className="px-4 py-3 text-gray-600">独自の製造方法</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">承認試験</td>
                      <td className="px-4 py-3 text-gray-600">簡略化</td>
                      <td className="px-4 py-3 text-gray-600">生物学的同等性試験必要</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-gray-900">安心感</td>
                      <td className="px-4 py-3 text-gray-600">非常に高い</td>
                      <td className="px-4 py-3 text-gray-600">高い</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">ジェネリック医薬品の選び方</h2>
            
            <p className="text-gray-700 mb-6">
              ジェネリック医薬品を選ぶ際のポイントをお伝えします。
              筋トレで自分に合った器具を選ぶように、薬も自分に合ったものを選ぶことが大切です。
            </p>

            <div className="bg-teal-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">選択のポイント</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">💊 薬剤師への相談</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• アレルギー歴の確認</li>
                    <li>• 現在の症状・効果の確認</li>
                    <li>• 他の薬との相互作用チェック</li>
                    <li>• 製剤の特徴説明を受ける</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🏥 医師との相談</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 病状の安定性確認</li>
                    <li>• 変更のタイミング相談</li>
                    <li>• 経過観察の方法確認</li>
                    <li>• 不安や疑問の解消</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📋 製剤情報の確認</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 添加物の内容確認</li>
                    <li>• 製造メーカーの信頼性</li>
                    <li>• 剤形の使いやすさ</li>
                    <li>• 包装の工夫</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">💰 経済性の検討</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 薬代の削減効果</li>
                    <li>• 長期使用での総コスト</li>
                    <li>• 保険適用の確認</li>
                    <li>• 家計への影響</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">よくある質問と回答</h2>
            
            <p className="text-gray-700 mb-6">
              薬局でよく聞かれるジェネリック医薬品に関する質問にお答えします。
            </p>

            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Q&A</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Q: ジェネリックは効果が劣るのでは？</h4>
                  <p className="text-gray-600 text-sm">A: 生物学的同等性試験により、先発品と同等の効果が証明されています。有効成分は全く同じで、体内での働きも同等です。</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Q: 副作用は先発品と同じですか？</h4>
                  <p className="text-gray-600 text-sm">A: 基本的に同じですが、添加物の違いにより稀に異なる反応が起こる可能性があります。変化を感じたら薬剤師に相談してください。</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Q: 途中で先発品に戻すことはできますか？</h4>
                  <p className="text-gray-600 text-sm">A: 可能です。医師の処方箋に「変更不可」の記載がなければ、患者さんの希望で先発品に戻すことができます。</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Q: すべての薬にジェネリックがありますか？</h4>
                  <p className="text-gray-600 text-sm">A: 特許が切れた薬のみです。新しい薬や特殊な製剤にはジェネリックがない場合があります。</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Q: オーソライズドジェネリックはどこで分かりますか？</h4>
                  <p className="text-gray-600 text-sm">A: 薬剤師にお尋ねください。AGは製品名や添付文書で確認できます。先発メーカーのホームページでも情報提供されています。</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">まとめ</h2>
            
            <p className="text-gray-700 mb-6">
              ジェネリック医薬品は、経済的メリットが大きく、品質も先発品と同等です。
              特にオーソライズドジェネリックは、先発品と全く同じ品質でありながら価格が安いという優れた選択肢です。
            </p>

            <div className="bg-teal-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">ビースト君からのアドバイス</h3>
              <p className="text-gray-700">
                ジェネリック医薬品も筋トレと同じで、正しい知識があれば安心して使えます。
                「安いから効果が劣る」という先入観は捨てて、薬剤師と相談しながら最適な選択をしましょう。
                経済的負担を軽減しながら、しっかりと治療を続けることが一番大切です。
                分からないことがあったら、いつでも薬剤師に相談してくださいね！💪💊
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">関連記事</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/beast-blog/effective-medication" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20explaining%20medicine%20timing%20and%20proper%20medication%20usage%2C%20educational%20illustration%20with%20clock%20and%20medicine%20schedule%2C%20friendly%20cartoon%20style%2C%20professional%20healthcare%20setting&width=400&height=250&seq=beast-medication-timing-related4-updated&orientation=landscape"
                alt="薬の効果的な飲み方"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">薬の効果的な飲み方とタイミング</h3>
                <p className="text-gray-600">薬の効果を最大限に引き出すための正しい服用方法について解説します</p>
              </div>
            </Link>
            <Link to="/beast-blog/otc-selection" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20in%20pharmacy%20setting%20explaining%20OTC%20medicines%2C%20cartoon%20illustration%20with%20medicine%20shelves%20and%20friendly%20consultation%20scene&width=400&height=250&seq=beast-otc-selection-related2-updated&orientation=landscape"
                alt="OTC医薬品"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">市販薬選びで失敗しないコツ</h3>
                <p className="text-gray-600">薬局でよく聞かれる市販薬の選び方について解説します</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">ビースト君ブログ</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/beast-blog" className="hover:text-white cursor-pointer">記事一覧</Link></li>
                <li><Link to="/beast-blog/multidisciplinary-collaboration" className="hover:text-white cursor-pointer">多職種連携</Link></li>
                <li><Link to="/beast-blog/winter-mental-health" className="hover:text-white cursor-pointer">メンタルヘルス</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
